import { Component, Inject, ViewChild, viewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoadingService } from 'src/app/shared/services/loading.service';
import { ChatService } from '../../services/chat.service';
import { UserService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-chat-dialog',
  standalone: false,
  templateUrl: './chat-dialog.component.html',
  styleUrl: './chat-dialog.component.scss',
})
export class ChatDialogComponent {
  public userData: any;

  public message: string = '';
  public messages: any[] = [];
  public currentUser;
  public type: string = 'chat';
  public user;

  public userDataEmail: string;

  public onCall: boolean = false;
  public calling: boolean = false;
  public callDeclined: boolean = false;

  @ViewChild('localVideo', { static: false }) public localVideo: any;
  @ViewChild('remoteVideo', { static: false }) public remoteVideo: any;

  peerConnection: RTCPeerConnection;
  remoteStream: MediaStream;
  localStream: MediaStream;
  pendingCandidates: RTCIceCandidateInit[] = [];

  private callingTimeout: any = null;

  constructor(
    private loadingService: LoadingService,
    private matDialogRef: MatDialogRef<ChatDialogComponent>,
    private chatService: ChatService,
    private userService: UserService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.userData = data.user;
    this.type = data.type || 'chat';

    this.userDataEmail = this.userData?.email ?? this.userData;
  }

  async ngOnDestroy() {
    this.chatService.emit('call_ended', {
      targetUserEmail: this.userDataEmail,
      callerEmail: this.currentUser.email,
    });
    this.callEndedFunction();

    // Remove socket listeners to avoid duplicates
    this.chatService.off('call_ended');
    this.chatService.off('call_decline');
    this.chatService.off('call_answer');
    this.chatService.off('video_offer');
    this.chatService.off('video_answer');
    this.chatService.off('ice_candidate');
  }

  async ngOnInit() {
    this.currentUser = await this.userService.getMe();

    this.messages = await this.chatService.getMessages(
      this.currentUser.email,
      this.userDataEmail
    );

    this.chatService.initializeChatObservable(
      this.currentUser.email,
      this.userDataEmail
    );

    this.chatService.chat.subscribe((message) => {
      this.messages.unshift(message);
    });

    ///////////////////////////
    this.remoteStream = new MediaStream();

    this.chatService.on('video_offer', async ({ offer, sender }) => {
      this.loadingService.show();
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }
      if (this.localStream) {
        this.localStream.getTracks().forEach((track) => track.stop());
        this.localStream = null;
      }
      if (this.remoteStream) {
        this.remoteStream.getTracks().forEach((track) => track.stop());
        this.remoteStream = null;
      }
      if (this.localVideo?.nativeElement) {
        this.localVideo.nativeElement.srcObject = null;
      }
      if (this.remoteVideo?.nativeElement) {
        this.remoteVideo.nativeElement.srcObject = null;
      }
        
      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });
      this.pendingCandidates = [];
      this.onCall = true;

      this.peerConnection.ontrack = (event) => {
        if (event.streams && event.streams[0]) {
          if (this.remoteVideo?.nativeElement) {
            this.remoteVideo.nativeElement.srcObject = event.streams[0];
          }
        }
      };

      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.chatService.emit('ice_candidate', {
            targetUserEmail: this.userDataEmail,
            candidate: event.candidate,
          });
        }
      };

      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      if (this.localVideo) {
        this.localVideo.nativeElement.srcObject = this.localStream;
      }

      this.localStream.getTracks().forEach((track) => {
        this.peerConnection.addTrack(track, this.localStream);
      });

      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription(offer)
      );

      for (const candidate of this.pendingCandidates) {
        await this.peerConnection.addIceCandidate(
          new RTCIceCandidate(candidate)
        );
      }
      this.pendingCandidates = [];

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      this.chatService.emit('video_answer', {
        targetUserEmail: this.userDataEmail,
        answer: answer,
      });

      this.loadingService.hide();
    });

    this.chatService.on('video_answer', async ({ answer }) => {
      if (this.peerConnection) {
        await this.peerConnection.setRemoteDescription(
          new RTCSessionDescription(answer)
        );

        for (const candidate of this.pendingCandidates) {
          await this.peerConnection.addIceCandidate(
            new RTCIceCandidate(candidate)
          );
        }
        this.pendingCandidates = [];
      }
    });

    this.chatService.on('ice_candidate', async ({ candidate }) => {
      if (this.peerConnection) {
        if (
          this.peerConnection.remoteDescription &&
          this.peerConnection.remoteDescription.type
        ) {
          await this.peerConnection.addIceCandidate(
            new RTCIceCandidate(candidate)
          );
        } else {
          this.pendingCandidates.push(candidate);
        }
      }
    });

    this.chatService.on('call_answer', () => {
      // The callee accepted, start WebRTC negotiation
      this.stopCallingOverlay();
      this.type = 'video'; // Set type to video call
      this.startVideoCall();
    });

    this.chatService.on('call_decline', () => {
      this.stopCallingOverlay();
      this.callDeclined = true;
      setTimeout(() => {
        this.callDeclined = false;
        this.callEndedFunction();
      }, 3000);
    });

    this.chatService.on('call_ended', () => {
      this.stopCallingOverlay();
      this.callEndedFunction();
    });

    if (this.data.isAnswer) {
      this.chatService.emit('call_answer', {
        targetUserEmail: this.userDataEmail,
      });
    }
  }

  public closeDialog(): void {
    this.matDialogRef.close();
  }

  public async sendMessage() {
    try {
      this.chatService.sendMessage(
        this.currentUser.email,
        this.userDataEmail,
        this.message
      );

      this.message = '';
    } catch (error) {
      throw error;
    }
  }

  public async startCall() {
    this.calling = true;
    this.chatService.emit('call', {
      targetUserEmail: this.userDataEmail,
      callerEmail: this.currentUser.email,
    });
    this.callingTimeout = setTimeout(() => {
      if (this.calling) {
        this.callEndedFunction();
      }
    }, 20000);
  }

  public async startVideoCall() {
    this.onCall = true;
    this.loadingService.show();
    this.type = 'video'; // Set type to video call
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    this.localVideo.nativeElement.srcObject = stream;

    this.remoteStream = new MediaStream();
    this.remoteVideo.nativeElement.srcObject = this.remoteStream;

    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    this.peerConnection.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        this.remoteVideo.nativeElement.srcObject = event.streams[0];
      }
    };

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.chatService.emit('ice_candidate', {
          targetUserEmail: this.userDataEmail,
          candidate: event.candidate,
        });
      }
    };

    stream.getTracks().forEach((track) => {
      this.peerConnection.addTrack(track, stream);
    });

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);

    this.chatService.emit('video_offer', {
      targetUserEmail: this.userDataEmail,
      offer: offer,
    });

    this.loadingService.hide();
  }

  public startSoundCall() {}

  public isChatOpen(): boolean {
    return this.type === 'chat';
  }

  public isVideoCallOpen(): boolean {
    return this.type === 'video';
  }

  public callEndedFunction(): void {
    this.stopCallingOverlay();
    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // Stop local stream tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    // Stop remote stream tracks
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => track.stop());
      this.remoteStream = null;
    }

    // Clear video elements
    if (this.localVideo?.nativeElement) {
      this.localVideo.nativeElement.srcObject = null;
    }
    if (this.remoteVideo?.nativeElement) {
      this.remoteVideo.nativeElement.srcObject = null;
    }

    this.chatService.emit('call_ended', {
      targetUserEmail: this.userDataEmail,
      callerEmail: this.currentUser.email,
    });

    this.matDialogRef.close();
  }

  private stopCallingOverlay() {
    this.calling = false;
    if (this.callingTimeout) {
      clearTimeout(this.callingTimeout);
      this.callingTimeout = null;
    }
  }
}
